import { z } from 'zod'
import { calendarDaySchema } from '../validators/temporal.validator.js'
import { PAGE_NEEDS_LIMIT, paginatedQuery } from './pagination.input.js'

import { EVENT_STATUSES, eventStatusSchema, type EventStatus } from '../enums/event-lifecycle.enum.js'

/**
 * An event's status is partly derived and partly decided.
 *
 * `past` is a *date comparison against now*, so it cannot be stored — it would
 * be stale between being written and being read, and the boundary moves while
 * nobody is looking. That was the original argument for deriving all of it.
 *
 * `cancelled` is different in kind: somebody decided it. It comes from
 * `Event.lifecycle`, which is stored, indexed, and has a history — see
 * `lib/src/enums/event-lifecycle.enum.ts`. A canceled event keeps its date, so
 * deriving its status from that date would call it scheduled or past and put a
 * released booking back in the book.
 *
 * `enquiry` and `booked` are the lifecycle's own words. This filter used to say
 * `draft` and `scheduled`, which was a second vocabulary for one concept — and
 * the web app had a third, hard-coded copy that had already fallen behind. The
 * list lives in `event-lifecycle.enum.ts` now and this is a view of it, so a new
 * status cannot arrive in one place and not the others.
 */
export const eventStatusKeys = EVENT_STATUSES
export type EventStatusKey = EventStatus

/**
 * The status filter — the lifecycle's own words, and only those.
 *
 * `draft` and `scheduled` were this filter's private names for `enquiry` and
 * `booked` — a second vocabulary for one concept, with a third hard-coded copy
 * in `EventsFilters.tsx` that had already fallen behind (it was missing
 * `cancelled`). One list, in `event-lifecycle.enum.ts`, and this is a view of it.
 *
 * The old words were mapped rather than refused for one rollout, because a
 * query parameter is a wire contract and the two halves of a deploy are minutes
 * apart. The mapping is gone: nothing sends them, and the filter is component
 * state rather than a URL parameter, so there is no saved link carrying one
 * either.
 */
export const eventStatusKeySchema: z.ZodType<EventStatusKey> = eventStatusSchema

/**
 * What an events listing is asking for.
 *
 * Every field is what the app's filter chips already offered when this was done
 * in the browser; moving them here is what lets the list be paged, because a
 * filter applied after paging shows three rows of fifty and calls it the answer.
 *
 * The one rule that is not a field: **past events are hidden unless something
 * asks for them.** A status of `past`, a date bound, or a name search brings
 * them back. Without that, the default listing of a venue that has been running
 * for five years opens on its oldest week.
 */
/** The longest name fragment the event list searches for. The search box stops there too. */
export const EVENT_SEARCH_MAX = 200

export const eventListQuerySchema = paginatedQuery({
  venueId: z.string().uuid().optional(),
  status: eventStatusKeySchema.optional(),
  /** Inclusive day bounds, `yyyy-MM-dd`, read in the server's zone. */
  /** Inclusive day bounds. Days, not instants — see `temporal.validator.ts`. */
  from: calendarDaySchema.optional(),
  to: calendarDaySchema.optional(),
  /** A case-insensitive fragment of the event's name. */
  q: z.string().trim().min(1).max(EVENT_SEARCH_MAX).optional(),
  /** The company planning the event, or `none` for events without a planner. */
  planner: z.union([z.literal('none'), z.string().uuid()]).optional(),
  /**
   * The Social Tables event an event was imported from. For the importer, which
   * has to find the event it made last time whatever it has been renamed to —
   * and whether or not it is in the past, which the default listing hides.
   */
  prismEventUid: z.string().trim().min(1).max(200).optional(),
}).refine(...PAGE_NEEDS_LIMIT)

export type EventListQuery = z.infer<typeof eventListQuerySchema>

/**
 * Whether a listing should include events that have already happened.
 *
 * Shared so the rule is stated once: the API applies it, and the app's empty
 * state explains it ("Past events are under Status → Past") only when it is
 * actually in force.
 */
export function eventListHidesPast(query: EventListQuery): boolean {
  return (
    query.status === undefined &&
    query.from === undefined &&
    query.to === undefined &&
    query.q === undefined &&
    query.prismEventUid === undefined
  )
}
